<script setup lang="ts" generic="IncludePrimitive extends boolean = true, IncludeRelation extends boolean = true, Multiple extends boolean = false">
import type { CollectionFieldSelectionProps, ModelValueBase } from '#layers/data-model/types/collection-field-selection';
import type { SelectMenuProps } from '#layers/ui/public';
import { reactiveOmit } from '@vueuse/core';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCollectionFieldSelection } from '#layers/data-model/composables/use-collection-field-selection';

type ModelValue = ModelValueBase<IncludePrimitive, IncludeRelation, Multiple>;

interface Props extends CollectionFieldSelectionProps<IncludePrimitive, IncludeRelation, Multiple>, Pick<SelectMenuProps, 'clear' | 'ui' | 'variant' | 'trailingIcon' | 'size'> {}

const props: Props = withDefaults(defineProps<Props>(), {
	includePrimitiveFields: undefined,
	includeRelationFields: undefined,
	clear: true,
});

defineSlots<{
	default?: () => void;
}>();

const forwarded = reactiveOmit(props, 'collection', 'includePrimitiveFields', 'includeRelationFields', 'includeConstrainedFields', 'includeUnsupportedFields', 'filter');

const open = defineModel<boolean>('open');

const modelValue = defineModel<ModelValue>({ required: true });

const { items } = useCollectionFieldSelection(() => props, modelValue);
const { t } = useI18n();

const selectionText = computed(() => {
	const selectionCount = Array.isArray(modelValue.value) ? modelValue.value.length : 0;
	const selectionCountText = t('collection_field_select.n_fields_selected', selectionCount);

	if (selectionCount === items.value.length) {
		return t('collection_field_select.all_fields_selected');
	}
	else if (selectionCount <= 3 && Array.isArray(modelValue.value)) {
		// small enough count to show the field names and truncate them
		return modelValue.value
			.map((fieldId) => items.value.find((field) => field.id === fieldId)?.label)
			.filter(Boolean)
			.join(', ') || selectionCountText;
	}
	else {
		return selectionCountText;
	}
});
</script>

<template>
	<DSelectMenu
		v-bind="forwarded"
		v-model="modelValue as never"
		v-model:open="open"
		value-key="id"
		spellcheck="false"
		:filter-fields="['apiName', 'label']"
		:items
		:content="{ align: 'start' }"
	>
		<template v-if="multiple || !!$slots.default" #default>
			<slot>
				<span class="truncate">
					{{ selectionText }}
				</span>
			</slot>
		</template>
	</DSelectMenu>
</template>
